import { DeleteOperation, SaveOperation } from '@infuse/api/common';
import { CreateMachineUser, User } from '@infuse/iam/user/type';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import { getAxiosErrorMessage } from '@infuse/utils';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';

export const useGenericCreateMachineUser = ({
  apiFn,
  roleApiFn,
  deleteApiFn,
  forcedParentId
}: {
  apiFn: SaveOperation<Omit<CreateMachineUser, 'roles'>, User>;
  roleApiFn: SaveOperation<{ roleIds: string[]; replace: boolean }, never>;
  deleteApiFn: DeleteOperation<never>;
  forcedParentId?: string;
}) => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  const mutation = useMutation<User, unknown, { parentId?: string; user: CreateMachineUser }>({
    mutationFn: async ({ parentId, user }) => {
      if (forcedParentId) {
        parentId = forcedParentId;
      }

      try {
        const createdUser = await apiFn(parentId ? { id: parentId } : {}, { name: user.name });

        try {
          await roleApiFn(
            { id: parentId, userId: createdUser.id },
            { roleIds: user.roles, replace: true }
          );
        } catch (roleError) {
          try {
            await deleteApiFn(parentId ? { id: parentId, userId: createdUser.id } : { userId: createdUser.id });
          } catch (deleteError) {
            console.error('Failed to rollback machine user after role assignment failure', deleteError);
          }
          throw roleError;
        }

        notify({
          title: t('iam.users.create_machine_user.title'),
          message: t('iam.users.notify.create_machine_user_success.title')
        });

        return createdUser;
      } catch (e) {
        notify({
          type: UiNotificationType.ERROR,
          title: t('iam.users.create_machine_user.title'),
          message: getAxiosErrorMessage(e, t('iam.users.notify.create_machine_user_failed'))
        });
        throw e;
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
    }
  });

  return {
    ...mutation
  };
};
